/**
 * Firebase Analytics wrapper — the player-app port of clubmanager's
 * core/analytics.ts (same rules: `isSupported()`-gated init, every helper a
 * no-op when unavailable, GA4 snake_case params, no SDK imports elsewhere).
 *
 * Player events (the authoritative list — the previous one claimed `login`
 * and `sign_up`, which fired from nowhere for months, and omitted the six
 * funnel events entirely):
 *
 *   AUTH        `login` {method} · `sign_up` {method, linked_from_guest}
 *   FIRST RUN   `onboarding_completed` {level, has_phone, from_guest}
 *   FUNNEL      `share_pressed` · `preview_viewed` · `guest_join_started`
 *               `guest_join_failed` · `guest_join_completed`
 *               `guest_signup_started`   — see core/funnel.ts for the params
 *   MATCHES     `match_created` {has_court, category, privacy, has_price,
 *               duration_min} · `match_joined` · `match_left` · `match_cancelled`
 *   PLACES      `place_viewed` {place_id, city} · `court_request_sent`
 *   ROUTING     `page_view` {page_path}
 *
 * USER PROPERTIES (`setAnalyticsUserProperties`) ride on EVERY event in the
 * BigQuery export: `city`, `level_band`, `is_guest`. That is what makes the
 * warehouse sliceable — a dimension omitted from an event is unrecoverable,
 * because GA4 has no backfill.
 *
 * Nothing here may carry PII: no name, email or phone. `match_id`/`place_id`
 * are opaque; `city` and `level_band` are coarse by design.
 */
import {
  getAnalytics,
  isSupported,
  logEvent,
  setUserId,
  setUserProperties,
  type Analytics,
} from 'firebase/analytics'
import { logger } from '@upadelmatch/core'
import { app, env } from './firebase/client'

let analytics: Analytics | null = null
let initPromise: Promise<Analytics | null> | null = null

export function initAnalytics(): Promise<Analytics | null> {
  if (initPromise) return initPromise
  initPromise = (async () => {
    try {
      const supported = await isSupported()
      if (!supported) {
        logger.i('[analytics] not supported in this environment — skipping')
        return null
      }
      analytics = getAnalytics(app)
      logger.i(`[analytics] initialised (${env})`)
      return analytics
    } catch (err) {
      logger.e('[analytics] init failed', err)
      return null
    }
  })()
  return initPromise
}

/** Fire a custom event. Safe to call before init — drops silently. */
export function track(
  name: string,
  params: Record<string, string | number | boolean | undefined> = {},
): void {
  if (!analytics) return
  const cleaned: Record<string, string | number | boolean> = {}
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined) continue
    cleaned[k] = v
  }
  try {
    logEvent(analytics, name, cleaned)
  } catch (err) {
    logger.w(`[analytics] event ${name} failed`, err)
  }
}

/** Stamp the current auth uid on the analytics session. */
export function setAnalyticsUser(uid: string | null): void {
  if (!analytics) return
  try {
    setUserId(analytics, uid ?? null)
  } catch (err) {
    logger.w('[analytics] setUserId failed', err)
  }
}

/** Log an automatic `page_view` whenever the router commits a new route. */
export function trackPageView(path: string): void {
  track('page_view', { page_path: path })
}

/**
 * Stamp coarse, non-identifying attributes on the analytics session.
 *
 * These export to BigQuery as `user_properties` on EVERY event, which is what
 * makes the warehouse genuinely sliceable: without them, answering "did the
 * share loop work better in Granada than Málaga" would need `city` duplicated
 * onto every event, and any event that shipped without it is unrecoverable —
 * GA4 has no backfill.
 *
 * Deliberately coarse and non-identifying: a city, a level BAND (not the
 * numeric level), and whether the session is a guest. No name, email, phone or
 * uid — `setAnalyticsUser` already carries the uid, and nothing here should be
 * able to re-identify a player from an export.
 */
export function setAnalyticsUserProperties(props: {
  city?: string | null
  levelBand?: string | null
  isGuest?: boolean
}): void {
  if (!analytics) return
  const cleaned: Record<string, string> = {}
  if (props.city) cleaned['city'] = props.city
  if (props.levelBand) cleaned['level_band'] = props.levelBand
  if (props.isGuest !== undefined) cleaned['is_guest'] = String(props.isGuest)
  if (!Object.keys(cleaned).length) return
  try {
    setUserProperties(analytics, cleaned)
  } catch (err) {
    logger.w('[analytics] setUserProperties failed', err)
  }
}

/**
 * `login` and `sign_up` were DOCUMENTED in this file's header and fired by
 * nothing — found 2026-08-09 while enumerating what BigQuery would receive.
 * The cost was precise: "how many signed up this week" had only
 * `onboarding_completed` as a proxy (which misses anyone who abandons the
 * form), and "how many came back" was measured by nothing at all — the
 * retention half of the funnel, for an app whose whole open question is
 * whether the share loop produces players who stay.
 */
export function trackLogin(method: string): void {
  track('login', { method })
}

export function trackSignUp(method: string, linkedFromGuest = false): void {
  // `linked_from_guest` separates a share-door guest upgrading in place from a
  // cold signup — the two are the same event to GA4 and completely different
  // acquisition stories.
  track('sign_up', { method, linked_from_guest: linkedFromGuest })
}
